// HUMINT captures are immutable evidence (PRD-0032 SC-014, ADR-0045): only the
// subject purge may replace, re-extract or delete one. The API marks capture
// documents `is_capture` and refuses any request that touches one with 409
// `capture_immutable`, and one that would remove the person a subject with
// captures was extracted from — deleting or re-extracting its source document —
// with 409 `capture_subject_source`; these helpers let the document screens say so.
import type { TFunction } from 'i18next'
import { ApiError } from '@/services/api/client'
import { isVideoUnavailable } from '@/utils/video-file'

interface CaptureFlag {
  id: string
  is_capture?: boolean
  file_type?: string | null
}

/** The code of a 409 refusal; null for any other failure. */
function conflictCode(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const code = (error.data as { detail?: { code?: unknown } } | null | undefined)?.detail?.code
  return typeof code === 'string' ? code : null
}

/** The 409 `capture_immutable` refusal: the request touched capture documents, or the case holds captures. */
export function isCaptureImmutable(error: unknown): boolean {
  return conflictCode(error) === 'capture_immutable'
}

/** The translated reason a refused request touched captures or their subject's source; null for any other failure. */
export function captureRefusal(error: unknown, t: TFunction): string | null {
  switch (conflictCode(error)) {
    case 'capture_immutable':
      return t('documents.capture.refused')
    case 'capture_subject_source':
      return t('documents.capture.subjectSource')
    default:
      return null
  }
}

/** Exclude immutable captures and videos whose processing is temporarily unavailable. */
export function extractableIds(documents: readonly CaptureFlag[]): string[] {
  return documents
    .filter(document => !document.is_capture && !isVideoUnavailable(document.file_type))
    .map(document => document.id)
}

/** How many of the selected documents are captures. */
export function countSelectedCaptures(
  documents: readonly CaptureFlag[],
  selectedIds: ReadonlySet<string>
): number {
  return documents.filter(document => document.is_capture && selectedIds.has(document.id)).length
}
